import { useTranslation } from "react-i18next";
import ProcessTable from "../../../components/table/ProcessTable";
import {
  useGetAllDynamicWorkflows,
  useGetAllRoles,
  useGetDynamicWorkflowsById,
} from "../../../hooks/common/query";
import { useCallback, useEffect, useState } from "react";
import AssignRolesToProcessesCard from "../../../components/card/AssignRolesToProcessesCard";
import { Button } from "../../../components/button";
import { useAddUpdateDynamicWorkflow } from "../../../hooks/common/mutation";
import toast, { Toaster } from "react-hot-toast";
import BackButtonWrapper from "../../../components/button/BackButton/wrapper";

const DynamicWorkflowConfig = () => {
  const { t, i18n } = useTranslation();

  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState(1);
  const [pageSize, setPageSize] = useState<number>(5);
  const [count, setCount] = useState(0);

  const [selectedProcessId, setSelectedProcessId] = useState(0);
  const [allProcesses, setAllProcesses] = useState([]);

  const { data: processes } = useGetAllDynamicWorkflows({
    pageNumber: page,
    pageSize: pageSize,
  });
  const { data: roles } = useGetAllRoles();

  const { mutateAsync: AddUpdateDynamicWorkflow } =
    useAddUpdateDynamicWorkflow();

  useEffect(() => {
    if (processes) {
      setAllProcesses(processes.data || []);
      setPages(processes.pagination?.pages || 1);
      setCount(processes.pagination?.count || 0);
    }
  }, [setCount, setPages, processes]);

  const { data: process } = useGetDynamicWorkflowsById({
    encryptedId: selectedProcessId ? selectedProcessId.toString() : "",
  });

  const [choosenRoles, setChoosenRoles] = useState([
    {
      id: 0,
      label: t("selectAll"),
    },
  ]);
  const [filteredRoles, setFilteredRoles] = useState<
    { id: number; label: string }[]
  >([]);

  useEffect(() => {
    if (process && process.length > 0) {
      const seletedChoosenRoles =
        process?.map(
          (item: {
            n_RoleID: number;
            s_RoleName: string;
            s_RoleNameAr: string;
          }) => ({
            id: item.n_RoleID,
            label: i18n.language === "en" ? item.s_RoleName : item.s_RoleNameAr,
          })
        ) || [];
      setChoosenRoles([
        {
          id: 0,
          label: t("selectAll"),
        },
        ...seletedChoosenRoles,
      ]);
    } else {
      setChoosenRoles([
        {
          id: 0,
          label: t("selectAll"),
        },
      ]);
    }
  }, [i18n.language, process, t]);

  const onSubmit = useCallback(async () => {
    try {
      if (selectedProcessId === 0) {
        toast.error(t("selectProcess"));
        return;
      }
      const body = {
        stages: choosenRoles.slice(1).map((item, idx) => ({
          n_RoleID: item.id,
          n_OrderID: idx + 1,
          n_LocationID: 1,
        })),
        n_ProcessID: selectedProcessId,
      };

      await AddUpdateDynamicWorkflow(body);
      toast.success(t("processUpdated"));
    } catch (error) {
      toast.error(t("somethingWentWrong"));
    }
  }, [AddUpdateDynamicWorkflow, choosenRoles, selectedProcessId, t]);

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <div className="flex flex-col gap-4 md:gap-8">
        <BackButtonWrapper>
          <h1 className="text-xl md:text-2xl dark:text-white">
            {t("dynamicWorkflowConfig")}
          </h1>
        </BackButtonWrapper>

        <div className="bg-white p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
          <div>
            <ProcessTable
              roles={roles}
              processes={allProcesses}
              selectedProcessId={selectedProcessId}
              setSelectedProcessId={setSelectedProcessId}
              setChoosenRoles={setChoosenRoles}
              page={page}
              pages={pages}
              setPage={setPage}
              count={count}
              setFilteredRoles={setFilteredRoles}
              pageSize={pageSize}
              setPageSize={setPageSize}
            />
          </div>

          <AssignRolesToProcessesCard
            roles={roles}
            filteredRoles={filteredRoles}
            setFilteredRoles={setFilteredRoles}
            choosenRoles={choosenRoles}
            setChoosenRoles={setChoosenRoles}
          />

          <div>
            <Button type="button" onClick={onSubmit}>
              {t("save")}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default DynamicWorkflowConfig;
